import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { REPORT_CATEGORIES, REPORT_ITEM_TYPES, REPORT_STATUS } from "@book2class/shared/learning";
import type { ReportCategory, ReportItemType, ReportStatus } from "@book2class/shared/learning";
import { api, ApiError, formatDate } from "../api";

/** Categorias sugeridas conforme o tipo de item (o aluno pode escolher qualquer uma). */
const DEFAULT_CATEGORY: Record<ReportItemType, ReportCategory> = {
  lesson: "incorrect",
  question: "wrong_answer",
  flashcard: "incorrect",
  case_study: "incorrect",
  exercise: "wrong_answer",
  glossary: "incorrect",
  platform: "technical",
};

/** Botão discreto “Relatar problema”, com formulário em janela, para qualquer item do curso. */
export function ReportButton({
  courseId,
  itemType,
  itemId,
  itemLabel,
  compact = false,
}: {
  courseId: string;
  itemType: ReportItemType;
  itemId?: string | null;
  itemLabel?: string;
  compact?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState<ReportCategory>(DEFAULT_CATEGORY[itemType]);
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: "ok" | "error"; text: string } | null>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  async function send() {
    setBusy(true);
    setMsg(null);
    try {
      await api.post(`/learn/${courseId}/reports`, {
        itemType,
        itemId: itemId ?? null,
        category,
        description,
      });
      setMsg({
        kind: "ok",
        text: "Relato enviado ao professor. Você acompanha a resposta em “Meus relatos”.",
      });
      setDescription("");
    } catch (err) {
      setMsg({
        kind: "error",
        text: err instanceof ApiError ? err.message : "Não foi possível enviar agora.",
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <button
        type="button"
        className={compact ? "report-link" : "btn ghost small"}
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
          setMsg(null);
        }}
        title="Relatar um problema neste conteúdo"
      >
        ⚑ {compact ? "Relatar" : "Relatar problema"}
      </button>
      {open &&
        createPortal(
          <div
            className="modal-backdrop"
            role="dialog"
            aria-modal="true"
            aria-labelledby="report-title"
            onClick={() => setOpen(false)}
          >
            <div className="modal card card-pad" onClick={(e) => e.stopPropagation()}>
              <h2 id="report-title" style={{ fontSize: "1.2rem" }}>
                Relatar problema
              </h2>
              <p className="segment-meta">
                {REPORT_ITEM_TYPES[itemType]}
                {itemLabel ? `: ${itemLabel.slice(0, 140)}` : ""}
              </p>
              <div className="field">
                <label htmlFor="report-cat">Tipo de problema</label>
                <select
                  id="report-cat"
                  value={category}
                  onChange={(e) => setCategory(e.target.value as ReportCategory)}
                >
                  {Object.entries(REPORT_CATEGORIES).map(([k, v]) => (
                    <option key={k} value={k}>
                      {v}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label htmlFor="report-desc">O que está errado?</label>
                <textarea
                  id="report-desc"
                  rows={4}
                  placeholder="Ex.: a resposta indicada como correta diverge do ebook (p. 5)."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>
              {msg && <p className={`alert ${msg.kind}`}>{msg.text}</p>}
              <div className="actions" style={{ marginTop: 14 }}>
                <button type="button" className="btn ghost" onClick={() => setOpen(false)}>
                  Fechar
                </button>
                <button
                  type="button"
                  className="btn"
                  disabled={busy || description.trim().length < 5 || msg?.kind === "ok"}
                  onClick={() => void send()}
                >
                  {busy ? "Enviando…" : "Enviar relato"}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}

type MyReport = {
  id: string;
  itemType: ReportItemType;
  item: string | null;
  category: ReportCategory;
  description: string;
  status: ReportStatus;
  response: string | null;
  createdAt: string;
};

/** Relatos do aluno no curso, com a situação e a resposta do professor. */
export function MyReports({ courseId }: { courseId: string }) {
  const [list, setList] = useState<MyReport[] | null>(null);
  useEffect(() => {
    api
      .get<MyReport[]>(`/learn/${courseId}/reports`)
      .then(setList)
      .catch(() => setList([]));
  }, [courseId]);
  return (
    <section className="card card-pad">
      <div className="item-head">
        <h2 style={{ fontSize: "1.05rem", margin: 0 }}>Meus relatos</h2>
        <ReportButton courseId={courseId} itemType="platform" compact />
      </div>
      {!list?.length ? (
        <p className="segment-meta">
          Encontrou algo errado em uma aula, questão ou flashcard? Use “Relatar problema” no próprio item.
        </p>
      ) : (
        <ul className="report-list">
          {list.map((r) => (
            <li key={r.id}>
              <div className="row-between">
                <strong>{REPORT_CATEGORIES[r.category] ?? r.category}</strong>
                <span
                  className={`badge ${r.status === "resolved" ? "ok" : r.status === "dismissed" ? "" : "warn"}`}
                >
                  {REPORT_STATUS[r.status]}
                </span>
              </div>
              <div className="segment-meta">
                {REPORT_ITEM_TYPES[r.itemType]}
                {r.item ? `: ${r.item.slice(0, 90)}` : ""} · {formatDate(r.createdAt)}
              </div>
              <p>{r.description}</p>
              {r.response && <p className="report-response">Professor: {r.response}</p>}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
